/**
 * TenantDrawerContent: body of the AdminDrawer (a Carbon SidePanel) for a
 * monitored-tenant row.
 *
 * Renders the related values (Org / Last scan / Drift), detail fields, and
 * the action list that owns the multi-tenant admin-consent flow, all from
 * drawer-parts. The drawer shell (header, slide-in, Close) is owned by
 * AdminDrawer; this component is just the children.
 *
 * State ownership:
 *   - Mutations (`onboardTenant`, `updateTenant`, `deactivateTenant`,
 *     `testTenantAuth`, `initiateAdminConsent`, `verifyAndRecordConsent`,
 *     `revokeAdminConsent`, `sendOnboardingEmail`) live in `useTenants`
 *     and are passed in by the parent so React Query invalidation flows
 *     through the parent's hook instance.
 *   - The Edit TenantFormModal is mounted INSIDE this component because
 *     the open/close lifecycle is local to the drawer's action list.
 *   - Revoke consent and Deactivate ask first in a Carbon ConfirmDialog
 *     (danger), which stays open and busy until the mutation settles.
 *
 * Popup-blocker contract (CRITICAL):
 *   `useTenants.initiateAdminConsent` returns an `AdminConsentInitiateResponse`
 *   containing a `consentUrl`. The browser may block `window.open` if it is
 *   called after an `await` inside a click handler. We mirror the legacy
 *   `TenantManagement.tsx:handleInitiateConsent` pattern verbatim
 *   (await → window.open) — the legacy page has been shipping that exact
 *   shape in production with no popup-block complaints. Do not refactor to
 *   pre-compute the URL or wrap in `setTimeout`.
 *
 * DTO field gaps vs. the spec prompt:
 *   - `TenantDto` does NOT carry `organizationId` or `organizationName`.
 *     The Container `/tenantmanagement` endpoint never returns the linked
 *     Org. Per Guardian voice ("don't fabricate to fit the voice"), the
 *     "Organization" value renders the same honest stub used by
 *     `OrgDrawerContent` for "Average posture": "Not available yet".
 *     The `onPivot` prop stays in the interface so
 *     downstream wiring works once the wire shape grows the linkage.
 *   - `TenantDto.adminConsentGrantedAt` is the only signal we have without
 *     an extra `useTenantConsentStatus` round-trip; surfacing it is
 *     consistent with `derivePendingConsentCount` semantics.
 */

import { useMemo, useState } from 'react'

import type { TenantDto } from '@/hooks/useTenants'
import type { useTenants } from '@/hooks/useTenants'
import { lastAssessmentAt } from '@/lib/tenant-assessed'

import TenantFormModal from '@/components/AdminPortal/TenantFormModal'
import { tenantDecisionCopy, type TenantDecision } from '@/components/AdminPortal/confirm-copy'
import { Button } from '@/components/carbon/Button'
import { ConfirmDialog } from '@/components/carbon/ConfirmDialog'
import { FIELD_ON_LAYER_02 } from '@/components/carbon/fieldStyles'
import { TextInput } from '@/components/carbon/TextInput'
import {
  ActionFeedback as FeedbackLine,
  ActionList,
  ActionRow,
  DetailGrid,
  DrawerSection,
  MonoId,
  Muted,
} from '@/components/AdminPortal/drawer-parts'
import { formatLongDate, shortenId } from '@/components/AdminPortal/admin-format'
// PivotChip is intentionally NOT imported here. The Org pivot card is an
// honest stub today because TenantDto carries no organizationId — see the
// file-level note. Reintroduce when the wire shape grows the linkage.

/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

type Mutations = ReturnType<typeof useTenants>

export interface TenantDrawerContentProps {
  tenant: TenantDto
  onClose(): void
  /**
   * Cross-entity pivot. Currently only `'organizations'` is meaningful;
   * the second arg is the orgId to apply as a filter. See the file-level
   * note about the missing DTO linkage — this is wired for when the wire
   * shape grows the field.
   */
  onPivot(target: 'organizations', orgId: string): void

  // Mutations from useTenants — passed by parent for proper invalidation.
  onboardTenant: Mutations['onboardTenant']
  updateTenant: Mutations['updateTenant']
  deactivateTenant: Mutations['deactivateTenant']
  testTenantAuth: Mutations['testTenantAuth']
  initiateAdminConsent: Mutations['initiateAdminConsent']
  verifyAndRecordConsent: Mutations['verifyAndRecordConsent']
  revokeAdminConsent: Mutations['revokeAdminConsent']
  sendOnboardingEmail: Mutations['sendOnboardingEmail']
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function TenantDrawerContent({
  tenant,
  onClose,
  onPivot: _onPivot,
  updateTenant: _updateTenant, // unused: edits go through TenantFormModal -> api.*
  onboardTenant: _onboardTenant, // unused at drawer level (create lives in tab)
  deactivateTenant,
  testTenantAuth,
  initiateAdminConsent,
  verifyAndRecordConsent,
  revokeAdminConsent,
  sendOnboardingEmail,
}: TenantDrawerContentProps) {
  const [showEditModal, setShowEditModal] = useState(false)
  const [busyAction, setBusyAction] = useState<BusyAction>(null)
  const [actionFeedback, setActionFeedback] = useState<ActionFeedback | null>(null)
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [emailForm, setEmailForm] = useState({ recipientName: '', recipientEmail: '' })
  const [justCopied, setJustCopied] = useState(false)
  // Revoke consent and Deactivate ask first; the ConfirmDialog stays open (busy) until the mutation settles.
  const [confirm, setConfirm] = useState<TenantDecision | null>(null)

  const isMultiTenant = tenant.authenticationMode === 'MultiTenant'
  const consentGranted = Boolean(tenant.adminConsentGrantedAt)

  // The backend writes the creation stamp into lastScanAt until the first scan: that is no scan date.
  const lastScanLabel = useMemo(() => formatLongDate(lastAssessmentAt(tenant)), [tenant])
  const createdLabel = useMemo(() => formatLongDate(tenant.createdAt), [tenant.createdAt])
  const consentLabel = useMemo(
    () => composeConsentLabel(tenant.authenticationMode, tenant.adminConsentGrantedAt),
    [tenant.authenticationMode, tenant.adminConsentGrantedAt],
  )
  const shortTenantId = useMemo(() => shortenId(tenant.tenantId), [tenant.tenantId])

  /* ---------- Action handlers ---------- */

  const withFeedback = async (
    action: NonNullable<BusyAction>,
    fn: () => Promise<ActionFeedback>,
  ) => {
    setBusyAction(action)
    setActionFeedback(null)
    try {
      const result = await fn()
      setActionFeedback(result)
    } catch (err) {
      setActionFeedback({
        action,
        tone: 'error',
        message: err instanceof Error ? err.message : 'Something went wrong.',
      })
    } finally {
      setBusyAction(null)
    }
  }

  const handleTestAuth = () =>
    withFeedback('test-auth', async () => {
      const result = await testTenantAuth(tenant.tenantId)
      return {
        action: 'test-auth',
        tone: result.success ? 'success' : 'error',
        message: result.success
          ? `Connection OK. ${result.message}`
          : `Connection failed. ${result.message}`,
      }
    })

  /**
   * CRITICAL: This handler matches the legacy `handleInitiateConsent` in
   * `TenantManagement.tsx:103-117` exactly — `await` the URL, then call
   * `window.open` synchronously in the same async continuation. The
   * legacy shape has been live in prod without popup-block reports; do
   * not refactor.
   */
  const handleInitiateConsent = () =>
    withFeedback('initiate-consent', async () => {
      const response = await initiateAdminConsent(tenant.tenantId)
      window.open(response.consentUrl, '_blank')
      return {
        action: 'initiate-consent',
        tone: 'success',
        message:
          'Consent URL opened in a new tab. After the customer grants consent, return and verify.',
      }
    })

  const handleVerifyConsent = () =>
    withFeedback('verify-consent', async () => {
      const response = await verifyAndRecordConsent({ tenantId: tenant.tenantId })
      if (response.success) {
        return {
          action: 'verify-consent',
          tone: 'success',
          message: `Consent verified. ${response.permissionsGranted ?? 0} permissions recorded.`,
        }
      }
      return {
        action: 'verify-consent',
        tone: 'error',
        message:
          response.errorMessage ??
          'Consent could not be verified. The service principal may not exist in the tenant yet.',
      }
    })

  const handleRevokeConsent = async () => {
    await withFeedback('revoke-consent', async () => {
      await revokeAdminConsent(tenant.tenantId)
      return {
        action: 'revoke-consent',
        tone: 'success',
        message: `${tenant.name} is now using per-tenant authentication.`,
      }
    })
    setConfirm(null)
  }

  /**
   * Copy-consent-URL mirrors the legacy `handleCopyConsentUrl`
   * (TenantManagement.tsx:119-135): initiate the URL, then write to
   * clipboard. The clipboard write does NOT need to be at the user-gesture
   * moment in modern browsers when called from within a Promise chain that
   * started from a click handler.
   */
  const handleCopyConsentUrl = () =>
    withFeedback('copy-consent-url', async () => {
      const response = await initiateAdminConsent(tenant.tenantId)
      try {
        await navigator.clipboard.writeText(response.consentUrl)
        setJustCopied(true)
        window.setTimeout(() => setJustCopied(false), 2000)
        return {
          action: 'copy-consent-url',
          tone: 'success',
          message: 'Consent URL copied to clipboard.',
        }
      } catch {
        return {
          action: 'copy-consent-url',
          tone: 'error',
          message:
            'Could not copy to clipboard. Check browser permissions, or share the URL another way.',
        }
      }
    })

  const handleSendOnboardingEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!emailForm.recipientName.trim() || !emailForm.recipientEmail.trim()) return
    await withFeedback('send-onboarding-email', async () => {
      const response = await sendOnboardingEmail({
        tenantId: tenant.tenantId,
        request: {
          recipientEmail: emailForm.recipientEmail.trim(),
          recipientName: emailForm.recipientName.trim(),
        },
      })
      if (response.success) {
        setShowEmailForm(false)
        setEmailForm({ recipientName: '', recipientEmail: '' })
        return {
          action: 'send-onboarding-email',
          tone: 'success',
          message: `Onboarding email sent to ${response.sentTo}.`,
        }
      }
      return {
        action: 'send-onboarding-email',
        tone: 'error',
        message: response.message || 'The onboarding email could not be sent.',
      }
    })
  }

  const handleDeactivate = async () => {
    await withFeedback('deactivate', async () => {
      await deactivateTenant(tenant.tenantId)
      onClose()
      return {
        action: 'deactivate',
        tone: 'success',
        message: `${tenant.name} deactivated.`,
      }
    })
    setConfirm(null)
  }

  /* ---------- Render ---------- */

  const feedbackFor = (action: NonNullable<BusyAction>) =>
    actionFeedback?.action === action ? actionFeedback : null

  return (
    <div className="flex flex-col gap-6">
      {/* ── Related ────────────────────────────────────────────────────── */}
      {/*
        Org is an honest stub today: TenantDto carries no organizationId /
        organizationName. When the wire shape grows the linkage, swap this
        value for a <PivotChip> that calls
        `onPivot('organizations', tenant.organizationId)`.
      */}
      <DrawerSection title="Related" first>
        <DetailGrid
          items={[
            { label: 'Organization', value: <Muted>Not available yet</Muted> },
            { label: 'Last scan', value: lastScanLabel ?? <Muted>Not scanned yet</Muted> },
            { label: 'Drift', value: <Muted>Not available yet</Muted> },
          ]}
        />
      </DrawerSection>

      {/* ── Detail fields ──────────────────────────────────────────────── */}
      <DrawerSection title="Details">
        <DetailGrid
          items={[
            { label: 'Tenant ID', value: <MonoId value={tenant.tenantId} short={shortTenantId} /> },
            { label: 'Domain', value: tenant.domain ?? <Muted>Not recorded</Muted> },
            { label: 'Authentication mode', value: tenant.authenticationMode ?? <Muted>Not recorded</Muted> },
            { label: 'Consent status', value: consentLabel },
            { label: 'Notification email', value: tenant.notificationEmail ?? <Muted>Not set</Muted> },
            { label: 'Auto-task email', value: tenant.autoTaskEmail ?? <Muted>Not set</Muted> },
            { label: 'Drift alert email', value: tenant.driftAlertNotificationEmail ?? <Muted>Not set</Muted> },
            { label: 'Rejected-drift email', value: tenant.rejectedDriftNotificationEmail ?? <Muted>Not set</Muted> },
            { label: 'Created', value: createdLabel ?? <Muted>Not recorded</Muted> },
          ]}
        />
      </DrawerSection>

      {/* ── Actions ────────────────────────────────────────────────────── */}
      <DrawerSection title="Actions">
        <ActionList>
          <ActionRow
            label="Edit"
            description="Update name, domain, authentication mode, and notification emails."
            onClick={() => setShowEditModal(true)}
            disabled={busyAction !== null}
          />

          <ActionRow
            label="Test auth"
            description="Try a live read against the customer directory."
            onClick={handleTestAuth}
            disabled={busyAction !== null}
            feedback={feedbackFor('test-auth')}
          />

          {isMultiTenant && (
            <>
              <ActionRow
                label="Initiate consent"
                description="Open the admin-consent URL in a new tab for the customer's global administrator."
                onClick={handleInitiateConsent}
                disabled={busyAction !== null}
                feedback={feedbackFor('initiate-consent')}
              />
              <ActionRow
                label="Verify consent"
                description="Re-check the customer's grant when the callback was missed."
                onClick={handleVerifyConsent}
                disabled={busyAction !== null}
                feedback={feedbackFor('verify-consent')}
              />
              <ActionRow
                label="Copy consent URL"
                description="Share the URL with the customer's global administrator."
                onClick={handleCopyConsentUrl}
                disabled={busyAction !== null}
                trailing={justCopied ? <span className="text-helper text-text-helper">Copied</span> : null}
                feedback={!justCopied ? feedbackFor('copy-consent-url') : null}
              />
              {consentGranted && (
                <ActionRow
                  label="Revoke consent"
                  description="Switch this tenant back to per-tenant authentication."
                  onClick={() => setConfirm('revoke-consent')}
                  disabled={busyAction !== null}
                  danger
                  feedback={feedbackFor('revoke-consent')}
                />
              )}
            </>
          )}

          <ActionRow
            label="Send onboarding email"
            description="Email the admin-consent URL and setup steps to a named recipient."
            onClick={() => setShowEmailForm(prev => !prev)}
            disabled={busyAction !== null}
            feedback={!showEmailForm ? feedbackFor('send-onboarding-email') : null}
          />
          {showEmailForm && (
            <li className={`border-b border-line-subtle bg-layer-2 px-4 py-4 ${FIELD_ON_LAYER_02}`}>
              <form onSubmit={handleSendOnboardingEmail} className="flex flex-col gap-4">
                <TextInput
                  id="tdc-recipient-name"
                  label="Recipient name"
                  required
                  value={emailForm.recipientName}
                  onChange={e =>
                    setEmailForm(prev => ({ ...prev, recipientName: e.target.value }))
                  }
                  placeholder="Jane Doe"
                  disabled={busyAction === 'send-onboarding-email'}
                />
                <TextInput
                  id="tdc-recipient-email"
                  type="email"
                  label="Recipient email"
                  required
                  value={emailForm.recipientEmail}
                  onChange={e =>
                    setEmailForm(prev => ({ ...prev, recipientEmail: e.target.value }))
                  }
                  placeholder="admin@customer.com"
                  disabled={busyAction === 'send-onboarding-email'}
                />
                <div className="grid grid-cols-2 gap-px">
                  <Button
                    kind="secondary"
                    size="md"
                    fullWidth
                    onClick={() => {
                      setShowEmailForm(false)
                      setEmailForm({ recipientName: '', recipientEmail: '' })
                    }}
                    disabled={busyAction === 'send-onboarding-email'}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    kind="primary"
                    size="md"
                    fullWidth
                    loading={busyAction === 'send-onboarding-email'}
                  >
                    Send
                  </Button>
                </div>
                {actionFeedback?.action === 'send-onboarding-email' && (
                  <FeedbackLine result={actionFeedback} />
                )}
              </form>
            </li>
          )}

          <ActionRow
            label="Deactivate"
            description="Stop scanning this tenant. Reversible."
            onClick={() => setConfirm('deactivate')}
            disabled={busyAction !== null}
            danger
            feedback={feedbackFor('deactivate')}
          />
        </ActionList>
      </DrawerSection>

      {/* ── Edit modal ─────────────────────────────────────────────────── */}
      {showEditModal && (
        <TenantFormModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          editingTenant={tenant}
          onSuccess={() => setShowEditModal(false)}
        />
      )}

      {/* ── Revoke consent / Deactivate confirm (danger) ───────────────── */}
      {confirm && (
        <ConfirmDialog
          open
          {...tenantDecisionCopy(confirm, tenant.name)}
          loading={busyAction === confirm}
          onConfirm={() => void (confirm === 'revoke-consent' ? handleRevokeConsent() : handleDeactivate())}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                               */
/* -------------------------------------------------------------------------- */

type BusyAction =
  | null
  | 'test-auth'
  | 'initiate-consent'
  | 'verify-consent'
  | 'revoke-consent'
  | 'copy-consent-url'
  | 'send-onboarding-email'
  | 'deactivate'

interface ActionFeedback {
  action: NonNullable<BusyAction>
  tone: 'success' | 'error'
  message: string
}

function composeConsentLabel(
  mode: TenantDto['authenticationMode'],
  grantedAt: string | undefined,
): React.ReactNode {
  if (mode !== 'MultiTenant') {
    return <Muted>Per-tenant credentials</Muted>
  }
  const granted = formatLongDate(grantedAt)
  if (grantedAt) {
    return granted ? `Granted ${granted}` : 'Granted'
  }
  return <Muted>Needs consent</Muted>
}
